import React, { useEffect, useState } from 'react';
import { Loader2, Check, X } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const AdminApprovals = () => {
  const { toast } = useToast();
  const [tab] = useState('roles');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const path = tab === 'facilitators' ? '/admin/approvals/facilitators' : '/admin/approvals/roles';
      const res = await api.get(path, { params: { status: statusFilter } });
      setItems(res.data || []);
    } catch (e) { console.error('Failed to load approvals:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [tab, statusFilter]);

  const approve = async (id) => {
    const path = tab === 'facilitators'
      ? `/admin/approvals/facilitators/${id}/approve`
      : `/admin/approvals/roles/${id}/approve`;
    try {
      await api.post(path, { review_notes: null });
      toast({ title: 'Approved ✅' });
      fetchItems();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail }); }
  };

  const reject = async (id) => {
    const path = tab === 'facilitators'
      ? `/admin/approvals/facilitators/${id}/reject`
      : `/admin/approvals/roles/${id}/reject`;
    try {
      await api.post(path, { review_notes: rejectReason });
      toast({ title: 'Rejected' });
      setRejecting(null);
      setRejectReason('');
      fetchItems();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail }); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif-display text-4xl text-burgundy">Approval <span className="font-italic-display text-rose-italic">Queue</span></h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40">No items in this queue.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.id} className="p-5 rounded-2xl border border-rose/50 bg-white/70">
              {tab === 'facilitators' ? (
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      {it.photo_url && <img src={it.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />}
                      <div>
                        <div className="font-serif-display text-xl text-burgundy">{it.display_name}</div>
                        <div className="text-sm text-mahogany/70">{it.title || '—'}</div>
                      </div>
                    </div>
                    {it.bio && <p className="mt-3 text-sm text-burgundy/85">{it.bio}</p>}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(it.specializations || []).map((s) => <Chip key={s}>{s}</Chip>)}
                    </div>
                  </div>
                  {statusFilter === 'pending' && (
                    <ActionButtons
                      onApprove={() => approve(it.id)}
                      onReject={() => setRejecting(it.id)}
                      rejecting={rejecting === it.id}
                      rejectReason={rejectReason}
                      setRejectReason={setRejectReason}
                      onConfirmReject={() => reject(it.id)}
                      onCancelReject={() => setRejecting(null)}
                    />
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="font-serif-display text-xl text-burgundy">
                      {it.role_requested === 'ashram_coord' ? 'Ashram Coordinator' : 'Center Coordinator'}
                    </div>
                    <div className="text-sm text-mahogany/70">User: {it.user_id}</div>
                    {it.notes && <p className="mt-2 text-sm text-burgundy/85">{it.notes}</p>}
                  </div>
                  {statusFilter === 'pending' && (
                    <ActionButtons
                      onApprove={() => approve(it.id)}
                      onReject={() => setRejecting(it.id)}
                      rejecting={rejecting === it.id}
                      rejectReason={rejectReason}
                      setRejectReason={setRejectReason}
                      onConfirmReject={() => reject(it.id)}
                      onCancelReject={() => setRejecting(null)}
                    />
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Chip = ({ children }) => <span className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full bg-rose-soft text-mahogany">{children}</span>;

const ActionButtons = ({ onApprove, onReject, rejecting, rejectReason, setRejectReason, onConfirmReject, onCancelReject }) => (
  <div className="space-y-2">
    <div className="flex gap-2">
      <button onClick={onApprove} className="pill-btn bg-green-100 text-green-800 hover:bg-green-200">
        <Check className="w-4 h-4" /> Approve
      </button>
      <button onClick={onReject} className="pill-btn-outline text-red-700 border-red-300 hover:bg-red-50">
        <X className="w-4 h-4" /> Reject
      </button>
    </div>
    {rejecting && (
      <div className="p-3 bg-red-50 rounded-xl space-y-2 w-full">
        <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Reason for rejection" className="w-full bg-white rounded-lg px-3 py-2 border border-red-200 focus:outline-none" />
        <div className="flex gap-2">
          <button onClick={onConfirmReject} className="text-xs px-3 py-1.5 rounded-full bg-red-600 text-white">Confirm</button>
          <button onClick={onCancelReject} className="text-xs px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-700">Cancel</button>
        </div>
      </div>
    )}
  </div>
);

export default AdminApprovals;
